import { lazy, Suspense, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { createBrowserRouter, Outlet, useLocation, useNavigate } from 'react-router-dom';

import RootProvider from 'watchtower-ui/contexts/Root';
import { t } from 'watchtower-ui/locales/i18n';
import Unauthorized from 'watchtower-ui/pages/Unauthorized/Unauthorized';
import { URL_PREFIX, US_REDIRECT_URL } from 'watchtower-ui/utils/Constants';
import { isValidInternalPath, sanitizeSearchParams } from 'watchtower-ui/utils/utilityFunctions';

const SaturationCurvePage = lazy(
  () => import('watchtower-ui/pages/HistoricalPages/saturation_curve_v2/SaturationCurveV2'),
);

const DynamicDashboardPage = lazy(() => import('watchtower-ui/pages/HistoricalPages/DynamicDashboardPage'));
const HistoricalHome = lazy(() => import('watchtower-ui/pages/HistoricalPages/HistoricalHome'));
const HistoricalKpi = lazy(() => import('watchtower-ui/pages/HistoricalPages/kpi/HistoricalKpi'));
const HistoricalWaterfall = lazy(() => import('watchtower-ui/pages/HistoricalPages/waterfall/HistoricalWaterfall'));
const IndustryMarketShare = lazy(() => import('./pages/HistoricalPages/waterfall/IndustryMarketShare'));
const HistoricalDecomp = lazy(() => import('watchtower-ui/pages/HistoricalPages/decomp/HistoricalDecomp'));
const DriverAnalysis = lazy(() => import('watchtower-ui/pages/HistoricalPages/driver_analysis/DriverAnalysis'));
const MediaPerformances = lazy(
  () => import('watchtower-ui/pages/HistoricalPages/media_performances/MediaPerformances'),
);
const ResourceAllocationLayout = lazy(() => import('watchtower-ui/pages/ResourceAllocation/Layout'));
const InputImpactInsights = lazy(() => import('watchtower-ui/pages/HistoricalPages/input_impact/InputImpactInsights'));
const ProjectList = lazy(() => import('watchtower-ui/pages/ResourceAllocation/ProjectList/Index'));
const PlanMeasurement = lazy(() => import('watchtower-ui/pages/ResourceAllocation/PlanMeasurement/Index'));
const NewPlan = lazy(() => import('watchtower-ui/pages/ResourceAllocation/NewPlan/Index'));
const Homepage = lazy(() => import('watchtower-ui/pages/Homepage/Homepage'));
// LandingPage kept for potential future use - now using CountrySelectionPage as default
// const LandingPage = lazy(() => import('watchtower-ui/pages/LandingPage/LandingPage'));
const CountrySelectionPage = lazy(
  () => import('watchtower-ui/pages/LandingPage/CountrySelection/CountrySelectionPage'),
);
const RequestAccessPage = lazy(() => import('watchtower-ui/pages/LandingPage/CountrySelection/RequestAccessPage'));
const DocumentationPage = lazy(() => import('watchtower-ui/pages/Documentation/DocumentationPage'));
const DataDictionaryPage = lazy(() => import('watchtower-ui/pages/Documentation/DataDictionary'));
const Profile = lazy(() => import('watchtower-ui/pages/Profile/Profile'));
const AdherenceTrackerLayout = lazy(() => import('watchtower-ui/pages/AdherenceTracker/AdherenceTrackerLayout'));
const AdherenceTrackerPage = lazy(() => import('watchtower-ui/pages/AdherenceTracker/AdherenceTrackerPage'));
const OneYearPlanPage = lazy(() => import('watchtower-ui/pages/OneYearPlan/OneYearPlanPage'));
const NpsSurveyHost = lazy(() => import('watchtower-ui/pages/NpsSurvey/NpsSurveyHost'));
const NpsAdminPage = lazy(() => import('watchtower-ui/pages/NpsSurvey/admin/NpsAdminPage'));
const GlobalComingSoonStub = lazy(() => import('watchtower-ui/pages/GlobalWatchtower/components/ComingSoonStub'));
const GlobalRAComingSoonRoute = () => <GlobalComingSoonStub module="Resource Allocation" />;
const NotFound = lazy(() => import('watchtower-ui/components/common/404'));

const redirectWithQuery = (to: string) => {
  const Component = () => {
    const { search } = useLocation();
    const navigate = useNavigate();
    useEffect(() => {
      if (!isValidInternalPath(to)) {
        console.warn('Invalid redirect path detected:', to);
        return;
      }
      const sanitized = sanitizeSearchParams(search);
      const queryString = sanitized ? `?${sanitized}` : '';
      const safePath = `${to}${queryString}`;
      // deepcode ignore OR: 'to' parameter is validated above, search params are sanitized
      navigate(safePath, { replace: true });
    }, [navigate, search]);
    return null;
  };
  return Component;
};

// US redirect component - redirects to US Watchtower without MSAL
const USRedirect = () => {
  useEffect(() => {
    globalThis.location.href = US_REDIRECT_URL;
  }, []);
  return null;
};

const router = createBrowserRouter([
  // Public routes - No RootProvider, no auth required
  {
    path: `${URL_PREFIX}/`,
    element: <Outlet />,
    children: [
      {
        index: true,
        Component: CountrySelectionPage, // Changed from LandingPage - public country selection
      },
      {
        path: 'select-country',
        Component: CountrySelectionPage, // Alias for backward compatibility
      },
    ],
  },
  // Request access page - no RootProvider needed, RequestAccessPage handles auth directly
  {
    path: `${URL_PREFIX}/request-access`,
    Component: RequestAccessPage,
  },
  // US redirect - public route, no MSAL, redirects to US Watchtower
  {
    path: `${URL_PREFIX}/us`,
    Component: USRedirect,
  },
  {
    path: `${URL_PREFIX}/us/*`,
    Component: USRedirect,
  },
  {
    path: `${URL_PREFIX}/:countryCode`,
    element: (
      <RootProvider>
        {/* Its own boundaries. Every other lazy() here is a route Component, which suspends
            only when you navigate to it; this one sits on the layout, so without them the NPS
            chunk joins the critical path of every country page. Suspense covers a *pending*
            import; it does not catch a *rejected* one -- a stale chunk after a deploy throws,
            and with no error boundary at the app root that would take the country route down.
            The card is optional, so both failures degrade to simply not rendering it. */}
        <ErrorBoundary fallbackRender={() => null}>
          <Suspense fallback={null}>
            <NpsSurveyHost />
          </Suspense>
        </ErrorBoundary>
        <Outlet />
      </RootProvider>
    ),
    children: [
      {
        index: true,
        Component: Homepage,
        handle: {
          label: 'Homepage',
        },
      },
      {
        path: 'data-dictionary',
        Component: DataDictionaryPage,
        handle: {
          label: 'Data Dictionary',
        },
      },
      {
        path: 'help/:path',
        Component: DocumentationPage,
        handle: {
          label: 'Documentation',
        },
      },
      {
        path: 'profile',
        Component: Profile,
        handle: {
          label: t('common:profile'),
        },
      },
      {
        path: 'nps-surveys',
        Component: NpsAdminPage,
        handle: {
          label: t('common:npsSurveys'),
        },
      },
      {
        path: 'historical',
        Component: DynamicDashboardPage,
        handle: {
          label: t('common:historicalHome'),
        },
        children: [
          {
            index: true,
            Component: HistoricalHome,
          },
          {
            path: 'waterfall',
            Component: HistoricalWaterfall,
            handle: {
              label: t('common:waterfall'),
            },
          },
          {
            path: 'industry-market-share',
            Component: IndustryMarketShare,
            handle: { label: t('common:industryMarketShare') },
          },
          {
            path: 'kpi',
            Component: HistoricalKpi,
            handle: {
              label: t('common:spendEfficiencies'),
            },
          },
          {
            path: 'saturationcurve',
            Component: SaturationCurvePage,
            handle: {
              label: t('common:saturationCurve'),
            },
          },
          {
            path: 'decomp',
            Component: HistoricalDecomp,
            handle: {
              label: t('common:historicalDecomposition'),
            },
          },
          {
            path: 'roicurve',
            Component: redirectWithQuery('/optimization/plan'),
          },
          {
            path: 'input_impact',
            Component: redirectWithQuery('../input-impact'),
          },
          {
            path: 'input-impact',
            Component: InputImpactInsights,
            handle: {
              label: t('common:inputImpactInsights'),
            },
          },
          {
            path: 'driver-analysis',
            Component: DriverAnalysis,
            handle: {
              label: t('common:driverAnalysis'),
            },
          },
          {
            path: 'media-performances',
            Component: MediaPerformances,
            handle: {
              label: t('common:mediaPerformances'),
            },
          },
          {
            path: '*',
            Component: NotFound,
          },
        ],
      },
      {
        path: 'resource-allocation',
        handle: {
          label: t('resourceAllocation'),
        },
        Component: ResourceAllocationLayout,
        children: [
          {
            index: true,
            Component: ProjectList,
          },
          {
            path: 'plan-measurement',
            Component: redirectWithQuery('../plan-measurement/create'),
          },
          {
            path: 'plan-measurement/:id',
            handle: {
              label: t('pnl:planMeasurement'),
            },
            Component: PlanMeasurement,
          },
          {
            path: 'new-plan',
            Component: redirectWithQuery('../new-plan/create'),
          },
          {
            path: 'new-plan/:id',
            handle: {
              label: t('pnl:createNewPlan'),
            },
            Component: NewPlan,
          },
          {
            path: 'scorecard',
            handle: {
              label: '1YP Scorecard',
            },
            // Availability decides whether the card is advertised, not whether the page exists.
            Component: OneYearPlanPage,
          },
          {
            path: '*',
            Component: NotFound,
          },
        ],
      },
      {
        path: 'adherence',
        handle: {
          label: t('common:adherenceTracker'),
        },
        Component: AdherenceTrackerLayout,
        children: [
          {
            index: true,
            Component: AdherenceTrackerPage,
          },
          {
            path: '*',
            Component: NotFound,
          },
        ],
      },
      {
        path: 'unauthorized',
        Component: Unauthorized,
      },
      // Global Watchtower (doc 40) reuses the existing country HD routes
      // (`/historical/waterfall` etc.); `useWaterfallData` routes to the global
      // aggregator backend when selectedCountry === 'GLOBAL'. Only the RA stub
      // lives under its own path until the Phase 2 wizard ships.
      {
        path: 'coming-soon',
        Component: GlobalRAComingSoonRoute,
        handle: {
          label: 'Coming Soon',
        },
      },
      {
        path: '*',
        Component: NotFound,
      },
    ],
  },
  {
    path: '*',
    Component: NotFound,
  },
]);

export default router;
